// src/modules/production-scheduler/jobsReport/fieldRegistry.ts
/** Declarative catalogue of every filterable/sortable/displayable field.
 *  The configurator UI, view engine, columns, and export all walk this —
 *  a new field is one entry here, no UI changes (spec §4). */
import type { ApiOperation } from '../types/chart.types';
import type { JobReportRow, PartDetail, FieldSource, FieldType, FilterOp } from './viewTypes';
import type { AggregatedSalesOrder, SalesOrderLineAgg } from './salesOrders';
import { rfehState, daysLate } from './display';
import { getTenantUi, resolvePath, type TenantFieldDescriptor } from '../../../tenant/tenantUi';

export interface RegistryCtx {
  opsByJob: Map<string, ApiOperation[]>;
}

export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  source: FieldSource;
  /** Receives the entity its `source` names: a job row, a part, or an op. */
  get: (entity: any, ctx: RegistryCtx) => unknown;
  /** Shown as a table column / detail field. Pseudo-fields like partsSummary
   *  are displayable but not filterable. */
  filterable?: boolean;        // default true
  /** Enum fields list their options from data at configurator-open time. */
  enumValues?: (jobs: JobReportRow[], ctx: RegistryCtx) => string[];
}

export const OPERATORS_BY_TYPE: Record<FieldType, FilterOp[]> = {
  string: ['is', 'isAnyOf', 'contains', 'isNot', 'isEmpty'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
  date: ['before', 'after', 'on', 'between', 'isEmpty'],
  boolean: ['is'],
  enum: ['isAnyOf', 'isNoneOf'],
  rfeh: ['isAnyOf'],
};

export const OPERATOR_LABELS: Record<FilterOp, string> = {
  is: 'is', isNot: 'is not', isAnyOf: 'is any of', isNoneOf: 'is none of',
  contains: 'contains', isEmpty: 'is empty',
  eq: '=', neq: '≠', gt: '>', gte: '≥', lt: '<', lte: '≤',
  before: 'before', after: 'after', on: 'on', between: 'between',
};

const uniq = (vals: Array<string | null | undefined>): string[] =>
  [...new Set(vals.filter((v): v is string => !!v))].sort();

/** First op (by op number) carrying a stamped score — the job's tier/score. */
const firstScored = (ops?: ApiOperation[]): ApiOperation | undefined =>
  ops ? [...ops].sort((a, b) => (a.operationNumber ?? 0) - (b.operationNumber ?? 0))
    .find(o => o.scoreExplanation?.tier?.label) : undefined;

export const FIELD_REGISTRY: FieldDef[] = [
  // ── job fields ────────────────────────────────────────────────
  { key: 'jobNum', label: 'Job', type: 'string', source: 'job', get: j => j.jobNum },
  { key: 'status', label: 'Status', type: 'enum', source: 'job', get: j => j.status,
    enumValues: () => ['blocked', 'feasible'] },
  { key: 'currentStart', label: 'Start date', type: 'date', source: 'job', get: j => j.currentStart },
  { key: 'earliestStart', label: 'Earliest feasible', type: 'date', source: 'job', get: j => j.earliestStart },
  { key: 'latestOpDue', label: 'Latest op due', type: 'date', source: 'job', get: j => j.latestOpDue },
  { key: 'daysLate', label: 'Slip (days)', type: 'number', source: 'job', get: j => daysLate(j) },
  { key: 'released', label: 'Released', type: 'boolean', source: 'job', get: j => j.released },
  { key: 'firm', label: 'Firm', type: 'boolean', source: 'job', get: j => j.firm },
  { key: 'engineered', label: 'Engineered', type: 'boolean', source: 'job', get: j => j.engineered },
  { key: 'onHold', label: 'On Hold', type: 'boolean', source: 'job', get: j => j.onHold },
  { key: 'rfeh', label: 'RF', type: 'rfeh', source: 'job', get: j => rfehState(j) },
  // Optimizer-stamped analysis fields (from the job's ops) — addable on any
  // job list: priority tier/score from scoreExplanation, delay from
  // placementExplanation (max capacity-wait across the job's ops).
  { key: 'priorityTier', label: 'Priority tier', type: 'enum', source: 'job',
    get: (j, ctx) => firstScored(ctx?.opsByJob?.get(j.jobNum))?.scoreExplanation?.tier?.label ?? null,
    enumValues: (jobs, ctx) => uniq(jobs.map(j => firstScored(ctx?.opsByJob?.get(j.jobNum))?.scoreExplanation?.tier?.label)) },
  { key: 'priorityScore', label: 'Priority score', type: 'number', source: 'job',
    get: (j, ctx) => firstScored(ctx?.opsByJob?.get(j.jobNum))?.scoreExplanation?.total ?? null },
  { key: 'delayWd', label: 'Delay (wd)', type: 'number', source: 'job',
    get: (j, ctx) => {
      const ops = ctx?.opsByJob?.get(j.jobNum) ?? [];
      let max = 0;
      for (const op of ops) {
        const pe = op.placementExplanation;
        if (pe?.binding === 'capacity') max = Math.max(max, pe.totalDaysWaited ?? 0);
      }
      return max;
    } },
  { key: 'jobType', label: 'Type', type: 'enum', source: 'job', get: j => j.jobType,
    enumValues: jobs => uniq(jobs.map(j => j.jobType)) },
  { key: 'partNumber', label: 'Part number', type: 'string', source: 'job', get: j => j.partNumber },
  { key: 'materialText', label: 'Material text', type: 'string', source: 'job', get: j => j.materialText },
  { key: 'numLines', label: '# Lines in MRQ', type: 'number', source: 'job', get: j => j.numLines },
  { key: 'estimatedHours', label: 'Est. hours', type: 'number', source: 'job', get: j => j.estimatedHours },
  { key: 'salesOrderId', label: 'Sales order', type: 'string', source: 'job', get: j => j.salesOrderId },
  { key: 'salesOrderShipDate', label: 'SO ship date', type: 'date', source: 'job', get: j => j.salesOrderShipDate },
  { key: 'lateSOCount', label: '# late sales orders', type: 'number', source: 'job',
    get: j => (j.lateForSalesOrders ?? []).length },
  { key: 'hasMaterialIssue', label: 'Material issue', type: 'boolean', source: 'job', get: j => j.hasMaterialIssue },
  { key: 'hasLateIssue', label: 'Late issue', type: 'boolean', source: 'job', get: j => j.hasLateIssue },
  { key: 'hasDependencyIssue', label: 'Dependency issue', type: 'boolean', source: 'job', get: j => j.hasDependencyIssue },
  { key: 'hasAnyIssue', label: 'Any issue', type: 'boolean', source: 'job',
    get: j => j.hasMaterialIssue || j.hasLateIssue || j.hasDependencyIssue },
  { key: 'partsIssueCount', label: 'Parts with issues', type: 'number', source: 'job',
    get: j => (j.partsConsumed ?? []).filter((p: PartDetail) => p.isLimiting || p.isOverdue).length },
  { key: 'materialDecision', label: 'Material decision', type: 'enum', source: 'job',
    get: j => j.decision?.material ?? 'undecided',
    enumValues: () => ['adhere', 'ignore', 'undecided'] },
  { key: 'lateDecision', label: 'Late decision', type: 'enum', source: 'job',
    get: j => j.decision?.late ?? 'undecided',
    enumValues: () => ['adhere', 'ignore', 'undecided'] },
  // display-only pseudo-field for the master table ("2 of 5")
  { key: 'partsSummary', label: 'Parts', type: 'string', source: 'job', filterable: false,
    get: j => {
      const parts = j.partsConsumed ?? [];
      const total = parts.length;
      if (!total) return '—';
      const iss = parts.filter((p: PartDetail) => p.isLimiting || p.isOverdue).length;
      return `${iss} of ${total}`;
    } },
  { key: 'issues', label: 'Issues', type: 'string', source: 'job', filterable: false,
    get: j => {
      const parts: string[] = [];
      if (j.hasMaterialIssue) parts.push('material');
      if (j.hasLateIssue) parts.push('late');
      if (j.hasDependencyIssue) parts.push('dependency');
      return parts.join(', ');
    } },

  // ── part fields (entity = PartDetail) ─────────────────────────
  { key: 'partNum', label: 'Part', type: 'string', source: 'part', get: p => p.partNum },
  { key: 'partType', label: 'Part type', type: 'enum', source: 'part', get: p => p.type,
    enumValues: jobs => uniq(jobs.flatMap(j => (j.partsConsumed ?? []).map(p => p.type))) },
  { key: 'partDescription', label: 'Part description', type: 'string', source: 'part', get: p => p.description },
  { key: 'partActive', label: 'Part active', type: 'boolean', source: 'part', get: p => p.active },
  { key: 'partNeed', label: 'Need', type: 'number', source: 'part', get: p => p.need },
  { key: 'partEarliestFeasible', label: 'Part earliest feasible', type: 'date', source: 'part',
    get: p => p.earliestFeasibleDate },
  { key: 'partIsLimiting', label: 'Limiting', type: 'boolean', source: 'part', get: p => p.isLimiting },
  { key: 'partIsOverdue', label: 'Overdue', type: 'boolean', source: 'part', get: p => p.isOverdue },

  // ── op fields (entity = ApiOperation) ─────────────────────────
  { key: 'customer', label: 'Customer', type: 'enum', source: 'op', get: o => o.customer_name,
    enumValues: (_jobs, ctx) =>
      uniq([...ctx.opsByJob.values()].flat().map(o => o.customer_name)) },
  { key: 'resourceGroup', label: 'Resource group', type: 'enum', source: 'op', get: o => o.resourceGroupId,
    enumValues: (_jobs, ctx) =>
      uniq([...ctx.opsByJob.values()].flat().map(o => o.resourceGroupId)) },
  { key: 'operationNumber', label: 'Op number', type: 'number', source: 'op', get: o => o.operationNumber },
  { key: 'orderType', label: 'Order type', type: 'enum', source: 'op', get: o => o.orderType,
    enumValues: (_jobs, ctx) =>
      uniq([...ctx.opsByJob.values()].flat().map(o => o.orderType)) },
  { key: 'opLocked', label: 'Op locked', type: 'boolean', source: 'op', get: o => !!o.locked },
  { key: 'opEstRemainingHours', label: 'Op est. remaining hrs', type: 'number', source: 'op',
    get: o => o.estimatedRemainingHours },
  { key: 'udCode', label: 'UD code', type: 'enum', source: 'op', get: o => o.udCodeDesc,
    enumValues: (_jobs, ctx) =>
      uniq([...ctx.opsByJob.values()].flat().map(o => o.udCodeDesc)) },

  // ── sales-order fields (entity = AggregatedSalesOrder) ────────
  // Drive the sales-orders-first leading column. Registered here so the view
  // configurator and engine treat them like any other column (filter + sort).
  { key: 'soId', label: 'Order', type: 'string', source: 'so', get: (o: AggregatedSalesOrder) => o.soId },
  { key: 'soCustomer', label: 'Customer', type: 'enum', source: 'so', get: (o: AggregatedSalesOrder) => o.customer,
    enumValues: (_jobs, ctx) =>
      uniq([...ctx.opsByJob.values()].flat().map(o => o.customer_name)) },
  { key: 'soShipDate', label: 'Ship date', type: 'date', source: 'so', get: (o: AggregatedSalesOrder) => o.shipDate },
  { key: 'soOverdueLines', label: 'Lines overdue', type: 'number', source: 'so', get: (o: AggregatedSalesOrder) => o.overdueLines },
  { key: 'soTotalLines', label: 'Total lines', type: 'number', source: 'so', get: (o: AggregatedSalesOrder) => o.totalLines },
  { key: 'soHasOverdue', label: 'Has overdue lines', type: 'boolean', source: 'so', get: (o: AggregatedSalesOrder) => o.overdueLines > 0 },

  // ── sales-order-line fields (entity = SalesOrderLineAgg) ──────
  { key: 'soLineNumber', label: 'Line', type: 'number', source: 'soLine', get: (l: SalesOrderLineAgg) => l.lineNumber },
  { key: 'soLinePart', label: 'Part', type: 'string', source: 'soLine', get: (l: SalesOrderLineAgg) => l.part },
  { key: 'soLineShipDate', label: 'Ship date', type: 'date', source: 'soLine', get: (l: SalesOrderLineAgg) => l.shipDate },
  { key: 'soLineJobsWithIssues', label: 'Jobs with issues', type: 'number', source: 'soLine', get: (l: SalesOrderLineAgg) => l.jobsWithIssues },
  { key: 'soLineTotalJobs', label: 'Total jobs', type: 'number', source: 'soLine', get: (l: SalesOrderLineAgg) => l.totalJobs },
  { key: 'soLineIsOverdue', label: 'Overdue', type: 'boolean', source: 'soLine', get: (l: SalesOrderLineAgg) => l.isOverdue },
];

// ── Tenant Pack extension ─────────────────────────────────────────
// Applied at module evaluation, which happens on first navigation into the
// scheduler — after createApp() installed the pack. Tenant fields surface
// data the tenant's sync attached under the doc's `ext` namespace; label
// overrides re-word Core fields (e.g. 'Job' → 'MO'); hidden fields disappear
// from configurator, columns and filters alike.
const ENTITY_TO_SOURCE: Record<TenantFieldDescriptor['entity'], FieldDef['source']> = {
  job: 'job', part: 'part', op: 'op', salesOrder: 'so', soLine: 'soLine',
};

function applyTenantPack(): void {
  const pack = getTenantUi();
  for (const [key, label] of Object.entries(pack.fieldLabels ?? {})) {
    const f = FIELD_REGISTRY.find(x => x.key === key);
    if (f) f.label = label;
  }
  if (pack.hiddenFields?.length) {
    const hidden = new Set(pack.hiddenFields);
    for (let i = FIELD_REGISTRY.length - 1; i >= 0; i--) {
      if (hidden.has(FIELD_REGISTRY[i].key)) FIELD_REGISTRY.splice(i, 1);
    }
  }
  for (const d of pack.fields ?? []) {
    if (FIELD_REGISTRY.some(f => f.key === d.key)) continue; // no collisions
    FIELD_REGISTRY.push({
      key: d.key,
      label: d.label,
      type: d.type ?? 'string',
      source: ENTITY_TO_SOURCE[d.entity] ?? 'job',
      get: (entity: any) => resolvePath(entity, d.source),
      filterable: d.filterable !== false,
      ...(d.type === 'enum' ? {
        enumValues: (_jobs: any, ctx: RegistryCtx) =>
          [...new Set([...ctx.opsByJob.values()].flat()
            .map(o => resolvePath(o, d.source))
            .filter((v): v is string => typeof v === 'string' && !!v))].sort(),
      } : {}),
    });
  }
}
applyTenantPack();

const byKey = new Map(FIELD_REGISTRY.map(f => [f.key, f]));
export function fieldByKey(key: string): FieldDef | undefined {
  return byKey.get(key);
}
